import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";

const STATS = [
  { value: "99.8%", label: "On-Time Rate" },
  { value: "<45 min", label: "STAT Pickup" },
  { value: "100%", label: "HIPAA + OSHA 29" },
];

export const Hero = () => {
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 700], [0, 110]);

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden bg-[#081A3F]">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -top-20">
        <img
          src="/assets/hero-hospital-dusk.jpeg"
          alt="Hospital campus at dusk with an HLM Courier Sprinter van parked at the entrance"
          data-testid="hero-hospital-image"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#081A3F]/95 via-[#081A3F]/78 to-[#081A3F]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#081A3F] via-transparent to-[#081A3F]/50" />
      </motion.div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-40 pb-24 lg:pt-56 lg:pb-36">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            data-testid="hero-region-line"
            className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-blue-300"
          >
            Servicing DC · Maryland · Virginia
          </motion.p>

          <h1 className="mt-6 font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
            <MaskedLine delay={0.15}>Vital Specimens.</MaskedLine>
            <MaskedLine delay={0.3}>
              <span className="text-[#5C9DFF]">Zero Margin.</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
          >
            HIPAA-compliant medical courier services specializing in pharmacy delivery, stat lab
            specimens, and chemotherapy hazardous drug transportation.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a href="#quote" data-testid="hero-dispatch-cta"
               className="group inline-flex items-center gap-2.5 rounded-full bg-[#1E6FFF] px-7 py-4 text-sm font-bold text-white shadow-xl shadow-blue-600/30 hover:bg-[#0F4C81] transition-colors duration-200">
              Schedule a Dispatch
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <a href="tel:5718509387" data-testid="hero-call-cta"
               className="inline-flex items-center gap-2.5 rounded-full glass-dark px-7 py-4 text-sm font-bold text-white hover:border-blue-400/50 transition-colors duration-200">
              <Phone className="h-4 w-4" /> (571) 850-9387
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-12 flex flex-wrap gap-3"
          >
            {STATS.map((s) => (
              <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                   className="glass-dark rounded-2xl px-5 py-3.5">
                <p className="font-display text-xl font-extrabold text-white leading-none">{s.value}</p>
                <p className="mt-1.5 text-[11px] font-mono font-semibold uppercase tracking-[0.16em] text-slate-400">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
